/**
 * @file setting-control.tsx
 * @module app/modules/setting/components/setting-control
 * @description Resolves and renders the appropriate input control for a
 * setting item based on its type/control metadata.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.3.5
 * @version 2.1.2
 */

import { Button, Form } from 'react-bootstrap';
import type { IFlagItem } from '../types/configurations.types';

/**
 * Props for the SettingControl component.
 */
interface ISettingControlProps {
  item: IFlagItem;
  value: any;
  updating: boolean;
  onExecuteAction: (item: IFlagItem) => void;
  onChange: (item: IFlagItem, nextValue: any) => void;
}

/**
 * Resolves control component by item metadata.
 * @param {ISettingControlProps} props Control props.
 * @returns {JSX.Element} Control component.
 */
const SettingControl = ({ item, value: currentValue, updating, onExecuteAction, onChange }: ISettingControlProps) => {
  const controlType = item.control || (item.type === 'bool' ? 'switch' : item.type === 'int' ? 'number' : 'text');

  if (item.type === 'action' || controlType === 'button') {
    return (
      <Button
        className="uw-btn uw-btn-primary"
        disabled={updating}
        onClick={() => onExecuteAction(item)}
      >
        {updating ? 'Processing...' : 'Execute'}
      </Button>
    );
  }

  const normalizedOptions = (item.options || []).map((option) => {
    if (typeof option === 'string') {
      return { label: option, value: option };
    }

    return {
      label: String(option?.label || option?.value || ''),
      value: String(option?.value || ''),
    };
  }).filter((option) => option.label && option.value);

  if (controlType === 'switch') {
    const checked = Boolean(currentValue);
    return (
      <Form.Check
        className="setting-control-switch"
        type="switch"
        checked={checked}
        disabled={updating}
        onChange={(e) => onChange(item, e.target.checked)}
      />
    );
  }

  if (controlType === 'dropdown') {
    const optionValues = normalizedOptions.length
      ? normalizedOptions
      : [{ label: 'NO', value: 'NO' }, { label: 'YES', value: 'YES' }];

    const fallbackValue = optionValues[0]?.value || '';
    return (
      <Form.Select
        value={String(currentValue ?? fallbackValue)}
        disabled={updating}
        onChange={(e) => onChange(item, e.target.value)}
      >
        {optionValues.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    );
  }

  if (controlType === 'multi-select') {
    const selectedValues = String(currentValue || '')
      .split(',')
      .map((value) => value.trim())
      .filter(Boolean);

    return (
      <Form.Select
        multiple
        value={selectedValues}
        disabled={updating}
        onChange={(e) => {
          const selected = Array.from(e.target.selectedOptions).map((option) => option.value);
          onChange(item, selected.join(','));
        }}
      >
        {normalizedOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </Form.Select>
    );
  }

  if (controlType === 'number') {
    return (
      <Form.Control
        type="number"
        value={Number(currentValue ?? 0)}
        disabled={Boolean(item.isDisabled) || updating}
        onChange={(e) => onChange(item, Number(e.target.value))}
      />
    );
  }

  if (controlType === 'textarea') {
    return (
      <Form.Control
        as="textarea"
        rows={3}
        value={String(currentValue ?? '')}
        disabled={updating}
        onChange={(e) => onChange(item, e.target.value)}
      />
    );
  }

  return (
    <Form.Control
      type="text"
      value={String(currentValue ?? '')}
      disabled={updating}
      onChange={(e) => onChange(item, e.target.value)}
    />
  );
};

export default SettingControl;
